import { useMemo } from "react";
import useAuth from "./useAuth";
import { SEVEN_DAYS_TIME, THIRTY_DAYS_TIME } from "@/constants/timeouts";
import { SubscriptionStatus } from "@/types/subscription";

export interface AccessStatus {
  hasAccess: boolean;
  accessEndsAt: Date | null;
  daysRemaining: number | null;
  status: SubscriptionStatus;
  isGracePeriod: boolean;
  isLegacyUser: boolean;
  cancelAtPeriodEnd: boolean;
}

export function useSubscriptionAccess(): AccessStatus {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) {
      return {
        hasAccess: false,
        accessEndsAt: null,
        daysRemaining: null,
        status: SubscriptionStatus.EXPIRED,
        isGracePeriod: false,
        isLegacyUser: false,
        cancelAtPeriodEnd: false,
      };
    }

    const now = new Date();
    let accessEndsAt: Date | null = null;
    let hasAccess = false;
    let status = SubscriptionStatus.EXPIRED;
    let isGracePeriod = false;

    // Metadata source
    const metadata = user.user_metadata || {};

    // Parse subscription data
    const subStatus = metadata.subscription_status as string | undefined;
    const subEndsAt = metadata.subscription_ends_at
      ? new Date(metadata.subscription_ends_at)
      : null;

    // Parse legacy user data
    const isLegacyUser = metadata.is_legacy_user === true;
    const legacyAccessUntil = metadata.legacy_access_until
      ? new Date(metadata.legacy_access_until)
      : null;

    // Parse trial data
    const trialEndsAt = metadata.trial_ends_at
      ? new Date(metadata.trial_ends_at)
      : null;
    // Fallback: used only if trial_ends_at doesn't exist in user metadata (pre-trigger users).
    const implicitTrialEnd = new Date(
      new Date(user.created_at).getTime() + THIRTY_DAYS_TIME,
    );

    // Parse cancellation data
    const cancelAtPeriodEnd = metadata.cancel_at_period_end === true;

    // Determine if user ever had a subscription (to prevent trial after cancel)
    const hadSubscription = !!subStatus;

    // ============================================
    // PRIORITY 1: Active Subscription
    // ============================================
    if (
      subStatus === SubscriptionStatus.ACTIVE ||
      subStatus === SubscriptionStatus.TRIALING
    ) {
      hasAccess = true;
      status = subStatus === SubscriptionStatus.TRIALING
        ? SubscriptionStatus.TRIALING
        : SubscriptionStatus.ACTIVE;
      accessEndsAt = subEndsAt;
    } // ============================================
    // PRIORITY 2: Legacy User with Grace Period
    // ============================================
    else if (isLegacyUser && legacyAccessUntil && now < legacyAccessUntil) {
      // Legacy users without active subscription get grace period access
      // "trial_active" / "trial_expired" are migration-set values (not real Stripe statuses)
      // and should be treated the same as having no subscription.
      // "incomplete" is included because the webhook may temporarily set this status
      // while the checkout payment is being confirmed — legacy users should retain
      // grace period access during that brief window.
      const hasNoRealSubscription = !subStatus ||
        subStatus === SubscriptionStatus.TRIAL_ACTIVE ||
        subStatus === SubscriptionStatus.TRIAL_EXPIRED ||
        subStatus === SubscriptionStatus.CANCELED ||
        subStatus === SubscriptionStatus.EXPIRED ||
        subStatus === SubscriptionStatus.PAST_DUE ||
        subStatus === SubscriptionStatus.UNPAID ||
        subStatus === SubscriptionStatus.INCOMPLETE;
      if (hasNoRealSubscription) {
        hasAccess = true;
        status = SubscriptionStatus.LEGACY_GRACE_PERIOD;
        accessEndsAt = legacyAccessUntil;
      }
    } // ============================================
    // PRIORITY 3: Canceled Subscription (within paid period)
    // ============================================
    else if (
      subStatus === SubscriptionStatus.CANCELED && subEndsAt && now < subEndsAt
    ) {
      hasAccess = true;
      status = SubscriptionStatus.CANCELED;
      accessEndsAt = subEndsAt;
    } // ============================================
    // PRIORITY 4: Past Due (Grace Period of 7 days)
    // ============================================
    else if (subStatus === SubscriptionStatus.PAST_DUE && subEndsAt) {
      const gracePeriodEnd = new Date(
        subEndsAt.getTime() + SEVEN_DAYS_TIME,
      );
      if (now < gracePeriodEnd) {
        hasAccess = true;
        status = SubscriptionStatus.PAST_DUE;
        accessEndsAt = gracePeriodEnd;
        isGracePeriod = true;
      } else {
        status = SubscriptionStatus.UNPAID;
      }
    } // ============================================
    // PRIORITY 5: Trial (ONLY for new users without subscription history)
    // ============================================
    else if (!hadSubscription && !isLegacyUser) {
      const effectiveTrialEnd = trialEndsAt || implicitTrialEnd;
      if (now < effectiveTrialEnd) {
        hasAccess = true;
        status = SubscriptionStatus.TRIAL_ACTIVE;
        accessEndsAt = effectiveTrialEnd;
      } else {
        status = SubscriptionStatus.TRIAL_EXPIRED;
        accessEndsAt = effectiveTrialEnd;
      }
    } // ============================================
    // PRIORITY 6: No Access (default)
    // ============================================
    else {
      hasAccess = false;
      // Determine most appropriate status
      if (subStatus === SubscriptionStatus.CANCELED) {
        status = SubscriptionStatus.CANCELED;
        accessEndsAt = subEndsAt;
      } else if (subStatus === SubscriptionStatus.INCOMPLETE) {
        status = SubscriptionStatus.INCOMPLETE;
      } else if (subStatus === SubscriptionStatus.UNPAID) {
        status = SubscriptionStatus.UNPAID;
      } else if (
        isLegacyUser && legacyAccessUntil && now >= legacyAccessUntil
      ) {
        status = SubscriptionStatus.EXPIRED;
        accessEndsAt = legacyAccessUntil;
      } else {
        status = SubscriptionStatus.EXPIRED;
      }
    }

    const daysRemaining = accessEndsAt
      ? Math.ceil(
        (accessEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      )
      : null;

    return {
      hasAccess,
      accessEndsAt,
      daysRemaining,
      status,
      isGracePeriod,
      isLegacyUser,
      cancelAtPeriodEnd,
    };
    // Use specific metadata fields as dependencies to ensure recalculation
  }, [
    user,
    user?.user_metadata?.cancel_at_period_end,
    user?.user_metadata?.subscription_status,
  ]);
}
